<template>
  <div
    id="nav"
    v-click-outside="close"
    class="navigation-list"
    :class="[isToggled ? 'on' : 'off']"
  >
    <a
      id="navigation-toggle"
      ref="toggleButton"
      href="#"
      :class="'toggle' + (isToggled ? ' on' : ' off')"
      @click="toggle"
    >
      <icon
        v-if="isToggled"
        key="isOpen"
        name="fas fa-times"
      />
      <icon
        v-else
        key="isClosed"
        name="fas fa-bars"
      />
    </a>
    <div
      v-if="isToggled"
    >
      <div class="flex flex-col">
        <router-link
          id="nav-dashboard"
          :to="{name: 'dashboard'}"
          class="router-link"
        >
          <icon
            name="fas fa-house-user"
            container-class="navigation-icon-container"
          />Dashboard
        </router-link>
        <router-link
          id="nav-feed"
          :to="{name: 'feed'}"
          class="router-link"
        >
          <icon
            name="fas fa-clone"
            container-class="navigation-icon-container"
          />Feed
        </router-link>
        <router-link
          id="nav-videos"
          :to="{name: 'videos'}"
          class="router-link"
        >
          <icon
            name="fas fa-film"
            container-class="navigation-icon-container"
          />Videos
        </router-link>
        <router-link
          v-if="can('vote')"
          id="nav-vote"
          :to="{name: 'vote'}"
          class="router-link"
        >
          <icon
            name="fas fa-thumbs-up"
            container-class="navigation-icon-container"
          />Vote
        </router-link>
        <router-link
          v-if="can('upload')"
          id="nav-upload"
          :to="{name: 'upload'}"
          class="router-link"
        >
          <icon
            name="fas fa-file-upload"
            container-class="navigation-icon-container"
          />Upload
        </router-link>

        <hr class="navigation-rule">

        <router-link
          v-if="can('upload')"
          id="nav-my-uploads"
          :to="{name: 'my-uploads'}"
          class="router-link"
        >
          <icon
            name="fas fa-images"
            container-class="navigation-icon-container"
          />My uploads
        </router-link>
        <router-link
          id="nav-notifications"
          :to="{name: 'notifications'}"
          class="router-link"
        >
          <icon
            name="fas fa-bell"
            container-class="navigation-icon-container"
          />Notifications
          <span
            v-if="notification_count > 0"
            id="nav-notifications-count"
            class="navigation-badge"
          >{{ notification_count }}</span>
        </router-link>

        <template v-if="canManage">
          <hr class="navigation-rule">

          <a
            id="nav-manage"
            href="#"
            class="router-link"
            :aria-expanded="manage ? 'true' : 'false'"
            @click.prevent="manage = !manage"
          >
            <icon
              name="fas fa-book"
              container-class="navigation-icon-container"
            />Manage
          </a>
          <collapse-expand
            slot-wrapper-class="flex flex-col px-2 ml-4 navigation-subnav"
            :condition="manage"
          >
            <p class="navigation-group">
              Content
            </p>
            <router-link
              v-if="can('manage_categories')"
              id="nav-manage-categories"
              :to="{name: 'manage-categories'}"
              class="router-link"
            >
              <icon
                name="fas fa-layer-group"
                container-class="navigation-icon-container"
              />Categories
            </router-link>
            <router-link
              v-if="can('manage_uploads')"
              id="nav-manage-uploads"
              :to="{name: 'manage-uploads'}"
              class="router-link"
            >
              <icon
                name="fas fa-arrow-circle-up"
                container-class="navigation-icon-container"
              />Uploads
            </router-link>
            <router-link
              v-if="can('manage_uploads')"
              id="nav-manage-videos"
              :to="{name: 'manage-videos'}"
              class="router-link"
            >
              <icon
                name="fas fa-film"
                container-class="navigation-icon-container"
              />Videos
            </router-link>
            <router-link
              v-if="can('manage_categories')"
              id="nav-manage-tips-tricks-blurbs"
              :to="{name: 'manage-tips-trick-blurbs'}"
              class="router-link"
            >
              <icon
                name="fas fa-lightbulb"
                container-class="navigation-icon-container"
              />Tips, tricks &amp; blurbs
            </router-link>

            <p class="navigation-group">
              Activity
            </p>
            <router-link
              v-if="can('manage_uploads')"
              id="nav-manage-votes"
              :to="{name: 'manage-votes'}"
              class="router-link"
            >
              <icon
                name="fas fa-poll"
                container-class="navigation-icon-container"
              />Votes
            </router-link>
            <router-link
              v-if="can('manage_users')"
              id="nav-manage-comments"
              :to="{name: 'manage-comments'}"
              class="router-link"
            >
              <icon
                name="fas fa-comment"
                container-class="navigation-icon-container"
              />Comments
            </router-link>
            <router-link
              v-if="can('manage_users')"
              id="nav-manage-likes"
              :to="{name: 'manage-likes'}"
              class="router-link"
            >
              <icon
                name="fas fa-heart"
                container-class="navigation-icon-container"
              />Likes
            </router-link>

            <p class="navigation-group">
              People
            </p>
            <router-link
              v-if="can('manage_users')"
              id="nav-manage-users"
              :to="{name: 'manage-users'}"
              class="router-link"
            >
              <icon
                name="fas fa-users"
                container-class="navigation-icon-container"
              />Users
            </router-link>
          </collapse-expand>
        </template>

        <hr class="navigation-rule">

        <a
          id="logout"
          href="#"
          class="router-link"
          @click.prevent="$emit('logout')"
        >
          <icon
            name="fas fa-sign-out-alt"
            container-class="navigation-icon-container"
          />Logout
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { library, dom } from '@fortawesome/fontawesome-svg-core';
import { faBars } from '@fortawesome/free-solid-svg-icons/faBars';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faHouseUser } from '@fortawesome/free-solid-svg-icons/faHouseUser';
import { faClone } from '@fortawesome/free-solid-svg-icons/faClone';
import { faFileUpload } from '@fortawesome/free-solid-svg-icons/faFileUpload';
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons/faThumbsUp';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons/faSignOutAlt';
import { faBook } from '@fortawesome/free-solid-svg-icons/faBook';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons/faLayerGroup';
import { faArrowCircleUp } from '@fortawesome/free-solid-svg-icons/faArrowCircleUp';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faPoll } from '@fortawesome/free-solid-svg-icons/faPoll';
import { faFilm } from '@fortawesome/free-solid-svg-icons/faFilm';
import { faImages } from '@fortawesome/free-solid-svg-icons/faImages';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import { faHeart } from '@fortawesome/free-solid-svg-icons/faHeart';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons/faLightbulb';
import vClickOutside from 'click-outside-vue3';
import Icon from './Icon.vue';
import CollapseExpand from '../transitions/collapse.vue';

library.add(
  faBars,
  faTimes,
  faHouseUser,
  faClone,
  faFileUpload,
  faThumbsUp,
  faSignOutAlt,
  faBook,
  faLayerGroup,
  faArrowCircleUp,
  faUsers,
  faPoll,
  faFilm,
  faImages,
  faBell,
  faHeart,
  faComment,
  faLightbulb,
);

dom.i2svg();
dom.watch();

export default {
  name: 'PbcNavigation',
  components: {
    CollapseExpand,
    Icon,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    isToggled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:isToggled', 'logout'],
  data() {
    return {
      manage: false,
      notification_count: 0,
    };
  },
  computed: {
    canManage() {
      return this.user.role.manage_categories || this.user.role.manage_uploads || this.user.role.manage_users;
    },
  },
  mounted() {
    const vm = this;
    axios.get('/api/notifications/count')
      .then((response) => {
        vm.notification_count = response.data;
      });
    this.$root.mercure
      .channel(`notifications.${this.user.id}`)
      .listen('NotificationEvent', (e) => {
        vm.notification_count = e.count;
      });
  },
  methods: {
    can(permission) {
      return this.user.role[permission];
    },
    toggle() {
      this.$emit('update:isToggled', !this.isToggled);
    },
    close() {
      this.$emit('update:isToggled', false);
    },
  },
};
</script>

<style scoped>
  @reference "../../../css/app.css";

  .navigation-list {
    @apply text-lg px-4 py-2 absolute z-50;
    background-color: var(--surface);
    color: var(--text);
    top: 0;
    left: 0;
  }
  @media (min-width: 768px) {
    .navigation-list {
      @apply border-r pt-4 border-b-0 relative mt-20;
      border-color: var(--border);
      top: auto;
      left: auto;
    }
  }
  @media (min-width: 1024px) {
    .navigation-list {
      @apply mt-24;
    }
  }
  .navigation-list.on {
    @apply flex-col justify-end border-r border-b border-solid;
    border-color: var(--border);
  }
  @media (min-width: 768px) {
    .navigation-list.on {
      @apply w-1/2 flex-col justify-end border-b-0;
    }
  }
  @media (min-width: 1024px) {
    .navigation-list.on {
      @apply w-1/5;
    }
  }
  @media (min-width: 1280px) {
    .navigation-list.on {
      @apply w-1/6;
    }
  }
  .navigation-list.off {
    @apply shrink;
  }
  .navigation-list a, .navigation-list button {
    @apply block w-full no-underline
          text-left rounded-md py-3 px-4
          transition duration-150 ease-in-out;
    color: var(--text);
    font-weight: 600;
  }
  .navigation-list a:visited, .navigation-list button:visited {
    @apply no-underline;
    color: var(--text);
  }
  .navigation-list a:hover, .navigation-list a:active, .navigation-list button:hover, .navigation-list button:active {
    background-color: var(--surface-card);
    color: var(--text-strong);
  }
  .navigation-list a.router-link-active, .navigation-list button.router-link-active {
    @apply relative bg-transparent cursor-default;
    color: var(--text-strong);
  }
  .navigation-list a.router-link-active::after, .navigation-list button.router-link-active::after {
    @apply absolute inset-y-0 left-0 w-1 my-2 rounded-sm;
    background-color: var(--signal);
    content: "";
  }
  .navigation-list a.router-link-active:hover,
  .navigation-list a.router-link-active:active,
  .navigation-list a.router-link-active:visited,
  .navigation-list button.router-link-active:hover,
  .navigation-list button.router-link-active:active,
  .navigation-list button.router-link-active:visited {
    @apply bg-transparent;
  }
  .navigation-list a.toggle, .navigation-list button.toggle {
    @apply w-10 h-10 p-0 m-0 leading-10 text-center block;
  }
  .navigation-list a:focus, .navigation-list button:focus {
    outline: none;
  }

  /**
   * Long labels wrap rather than being clipped: the rail is a fixed fraction
   * of the viewport, and "Tips, tricks & blurbs" and a notification badge
   * both ran past its edge.
   */
  /**
   * Scoped under .navigation-list so it out-specifies the `a, button` rule
   * above, which sets display:block and would otherwise win.
   */
  .navigation-list .router-link {
    @apply flex items-center;
    line-height: 1.3;
    white-space: normal;
  }

  /**
   * Separates the main destinations from what is yours, from managing the
   * app, from leaving it.
   */
  .navigation-rule {
    @apply my-2 mx-4;
    border: 0;
    border-top: 1px solid var(--border);
  }

  /**
   * Only rendered above zero, so an empty inbox is not announced as "(0)".
   */
  .navigation-badge {
    @apply shrink-0 ml-2 px-2 rounded-full text-xs;
    margin-left: auto;
    background-color: var(--signal);
    color: var(--on-signal);
    font-variant-numeric: tabular-nums;
  }

  .navigation-group {
    @apply m-0 mt-2 mb-1 px-4 text-xs;
    color: var(--text-muted);
  }
</style>
